import { extendTheme, theme as originalTheme } from '@chakra-ui/react';
import type { StyleFunctionProps } from '@chakra-ui/theme-tools';

import {
  EMERALD_DARK,
  EMERALD_DEFAULT,
  EMERALD_LIGHT,
  SEMANTIC_BG_ACCENT,
  SEMANTIC_BG_HOVER,
  SEMANTIC_BG_INPUT,
  SEMANTIC_BG_SURFACE,
  SEMANTIC_BORDER_INPUT,
} from './colorConstants';

const EMERALD_FOCUS_RING = `0 0 0 3px rgba(12, 207, 124, 0.6)`;

const MENU_FONT_SIZE = 'sm';
const MENU_ITEM_HOVER_BG =
  'var(--color-bg-hover, var(--chakra-colors-gray-100))';

// Beats every hardcoded z-[9999] popover/dropdown (GenericCustomPopover and others) —
// a tooltip should always render above any surface it's describing.
const TOOLTIP_Z_INDEX = 10000;

export const theme = extendTheme({
  config: {
    initialColorMode: 'light',
    useSystemColorMode: false,
  },
  fonts: {
    heading: 'Roboto, sans-serif',
    body: 'Roboto, sans-serif',
  },
  shadows: {
    outline: EMERALD_FOCUS_RING,
  },
  components: {
    Radio: {
      variants: {
        emerald: {
          control: {
            _checked: {
              bgColor: EMERALD_LIGHT,
              borderColor: EMERALD_LIGHT,
              _hover: {
                bgColor: EMERALD_LIGHT,
                borderColor: EMERALD_LIGHT,
              },
            },
          },
        },
      },
    },
    Alert: {
      variants: {
        solid: (props: StyleFunctionProps) => {
          const { colorScheme: c } = props;
          if (c !== 'blue') {
            return originalTheme?.components?.Alert?.variants?.solid(props);
          }
          return {
            container: {
              bg: '#222222',
            },
          };
        },
      },
    },
    Button: {
      baseStyle: {
        borderRadius: 'var(--radius-semantic)',
        _focusVisible: {
          boxShadow: 'outline',
          outline: 'none',
        },
        _hover: {
          bg: SEMANTIC_BG_HOVER,
        },
        _disabled: {
          opacity: 0.4,
          cursor: 'not-allowed',
          _hover: {
            bg: 'inherit',
          },
        },
      },
      variants: {
        white: {
          border: '1px solid',
          borderColor: 'green.500',
          bg: 'white',
          color: 'green.500',
          _hover: {
            bg: 'green.50',
          },
          _disabled: {
            bg: 'white',
            opacity: 0.4,
            cursor: 'not-allowed',
            _hover: {
              bg: 'white',
            },
          },
        },
        ciano: {
          bgGradient: 'linear(to-r, cyan.500, cyan.700)',
          color: 'white',
          _hover: {
            bgGradient: 'linear(to-l, cyan.500, cyan.700)',
          },
          _disabled: {
            opacity: 0.4,
            cursor: 'not-allowed',
            _hover: {
              bgGradient: 'linear(to-r, cyan.500, cyan.700)',
            },
          },
        },
        emerald: {
          bg: EMERALD_DEFAULT,
          color: 'white',
          _hover: {
            bg: EMERALD_DARK,
            color: 'white',
          },
          _disabled: {
            bg: EMERALD_DEFAULT,
            opacity: 0.4,
            color: 'gray.300',
            cursor: 'not-allowed',
            _hover: {
              bg: EMERALD_DEFAULT,
              color: 'gray.300',
            },
          },
        },
        blackOutlineBorderless: {
          border: 'none',
          bg: 'transparent',
          color: 'var(--color-text-primary)',
          _hover: {
            bg: 'transparent',
            color: 'var(--color-text-primary)',
          },
          _disabled: {
            bg: 'transparent',
            opacity: 0.4,
            cursor: 'not-allowed',
            _hover: {
              bg: 'transparent',
            },
          },
        },
        emeraldOutline: {
          border: '1px solid',
          borderColor: EMERALD_DEFAULT,
          bg: 'transparent',
          color: EMERALD_DEFAULT,
          _hover: {
            bg: 'transparent',
            color: EMERALD_DARK,
          },
          _disabled: {
            bg: 'transparent',
            opacity: 0.4,
            cursor: 'not-allowed',
            _hover: {
              bg: 'transparent',
              color: EMERALD_DEFAULT,
            },
          },
        },
        lightGray: {
          bg: SEMANTIC_BG_INPUT,
          border: '1px solid',
          borderColor: SEMANTIC_BORDER_INPUT,
          color: 'var(--color-text-primary)',
          _hover: {
            bg: SEMANTIC_BG_HOVER,
          },
          _disabled: {
            bg: SEMANTIC_BG_INPUT,
            opacity: 0.4,
            cursor: 'not-allowed',
            _hover: {
              bg: SEMANTIC_BG_INPUT,
            },
          },
        },
        dangerOutline: {
          border: '1px solid',
          borderColor: 'var(--color-bg-danger, #DC2626)',
          bg: 'transparent',
          color: 'var(--color-text-danger, #EF4444)',
          _hover: {
            bg: 'rgba(153, 27, 27, 0.2)',
            color: 'var(--color-bg-danger, #DC2626)',
          },
          _disabled: {
            bg: 'transparent',
            opacity: 0.4,
            cursor: 'not-allowed',
            _hover: { bg: 'transparent' },
          },
        },
        danger: {
          bg: 'var(--color-bg-danger, #DC2626)',
          color: 'white',
          _hover: {
            bg: 'var(--color-bg-danger-fill-hover, #B91C1C)',
            color: 'white',
          },
          _disabled: {
            bg: 'var(--color-bg-danger, #DC2626)',
            opacity: 0.4,
            color: 'gray.300',
            cursor: 'not-allowed',
            _hover: {
              bg: 'var(--color-bg-danger, #DC2626)',
              color: 'gray.300',
            },
          },
        },
      },
      defaultProps: {
        variant: '',
      },
    },
    Avatar: {
      variants: {
        emerald: {
          container: {
            bg: EMERALD_DEFAULT,
          },
        },
      },
    },
    Spinner: {
      variants: {
        emerald: {
          color: EMERALD_DEFAULT,
        },
      },
      defaultProps: {
        variant: 'emerald',
      },
    },
    Switch: {
      variants: {
        emerald: {
          track: {
            _checked: {
              bg: EMERALD_LIGHT,
            },
          },
          thumb: {
            _checked: {
              bg: 'white',
            },
          },
        },
        // Mirrors ToggleChip's track/thumb tokens (bg-semantic-bg-accent,
        // border-semantic-border-input, bg-semantic-bg-surface) so a standalone
        // Switch reads as the same control.
        accent: {
          track: {
            bg: SEMANTIC_BORDER_INPUT,
            _checked: {
              bg: SEMANTIC_BG_ACCENT,
            },
          },
          thumb: {
            bg: SEMANTIC_BG_SURFACE,
          },
        },
      },
    },
    Modal: {
      baseStyle: {
        overlay: {
          bg: 'blackAlpha.800',
        },
      },
    },
    Tooltip: {
      baseStyle: {
        bg: EMERALD_DEFAULT,
        color: 'white',
        zIndex: TOOLTIP_Z_INDEX,
      },
    },
    Menu: {
      baseStyle: {
        list: {
          borderRadius: 'var(--radius-semantic)',
          fontSize: MENU_FONT_SIZE,
        },
        // apps/desktop ships no `--color-*` tokens, so every var here needs a
        // fallback or its menus render transparent/uncolored.
        item: {
          fontSize: MENU_FONT_SIZE,
          bg: 'transparent',
          color: 'var(--color-text-primary, inherit)',
          _hover: { bg: MENU_ITEM_HOVER_BG },
          _focus: { bg: MENU_ITEM_HOVER_BG },
          _active: { bg: MENU_ITEM_HOVER_BG },
          _expanded: { bg: MENU_ITEM_HOVER_BG },
        },
      },
    },
    Popover: {
      baseStyle: {
        content: {
          borderRadius: 'var(--radius-semantic)',
          fontSize: MENU_FONT_SIZE,
        },
      },
      variants: {
        // Lifts the popper wrapper (not just `content`, which only inherits
        // z-index) above a nested CustomModal. 2150 mirrors
        // Z_INDEX_MODAL_DROPDOWN in apps/web/src/utils/constants/uiConstants.ts
        // — kept as a literal here since libs/app-config can't import from apps/web.
        dropdown: {
          popper: {
            zIndex: 2150,
          },
        },
      },
    },
    Input: {
      variants: {
        lightGray: {
          field: {
            bg: SEMANTIC_BG_INPUT,
            border: '1px solid',
            borderColor: SEMANTIC_BORDER_INPUT,
          },
        },
      },
    },
    Select: {
      variants: {
        lightGray: {
          field: {
            bg: SEMANTIC_BG_INPUT,
            border: '1px solid',
            borderColor: SEMANTIC_BORDER_INPUT,
          },
        },
      },
    },
    Textarea: {
      variants: {
        lightGray: {
          bg: SEMANTIC_BG_INPUT,
          border: '1px solid',
          borderColor: SEMANTIC_BORDER_INPUT,
        },
      },
    },
  },
});
